// src/utils/partnerBrand.js
// Co-branding for partner (travel agent) links. RouteChef stays the seller: payment, invoice,
// ticket, cancellations and support are RouteChef's. The partner is shown as the person who
// planned the trip.
//
//   Partner signed in        -> links they share carry ?p=<public_ref> (withPartnerRef)
//   Customer opens that link -> that page and the booking pages opened from it show the
//                               partner's branding (usePartnerBrand); every other page is plain
//                               RouteChef. The ref is also remembered for 30 days for commission
//                               (hotel bookings send it as partner_ref).
//   Partner opens a page     -> they see their own branding, so they know what customers see.
//
// The ref is signed by the backend ("<user_id>.<signature>"), so it can't be guessed.
// Branding for a ref comes from the public intent "agent_public_brand".
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { API_URL, getAgentSession } from "./agentAuth";
import { useAgentBranding } from "./agentBranding";

export const PARTNER_PARAM = "p";
const REF_KEY = "rc_partner_ref";
const REF_TTL_MS = 30 * 24 * 3600 * 1000;
const BRAND_KEY = (ref) => `rc_partner_brand_${ref}`;
const BRAND_TTL_MS = 12 * 3600 * 1000;
const REF_RE = /^[a-z0-9._+-]{1,80}\.[A-Za-z0-9_-]{10}$/;

const read = (k) => { try { return JSON.parse(localStorage.getItem(k) || "null"); } catch { return null; } };
const write = (k, v) => { try { v == null ? localStorage.removeItem(k) : localStorage.setItem(k, JSON.stringify(v)); } catch { /* storage blocked */ } };

/** The signed-in partner's own ref ("" when no partner is signed in, or the backend is older). */
export function ownPartnerRef() {
    return getAgentSession()?.profile?.public_ref || "";
}

/** Remember ?p= from the current URL (last link wins). */
export function capturePartnerRef(search = typeof window !== "undefined" ? window.location.search : "") {
    try {
        const ref = new URLSearchParams(search).get(PARTNER_PARAM);
        if (ref && REF_RE.test(ref) && ref !== ownPartnerRef()) write(REF_KEY, { ref, at: Date.now() });
    } catch { /* bad URL */ }
}

/** Ref of the partner whose link brought this customer here, or "" (never for a signed-in partner). */
export function getPartnerRef() {
    if (getAgentSession()?.profile) return "";
    const v = read(REF_KEY);
    if (!v?.ref || Date.now() - (v.at || 0) > REF_TTL_MS) return "";
    return v.ref;
}

/**
 * URL to share, with the partner's code on it: the signed-in partner's own, or (when a customer
 * forwards a partner's page) the partner of the page they are on. Works with absolute or relative URLs.
 */
export function withPartnerRef(url) {
    const ref = ownPartnerRef() || getFlowPartnerRef();
    if (!ref || !url) return url;
    try {
        const u = new URL(url, window.location.origin);
        u.searchParams.set(PARTNER_PARAM, ref);
        return /^https?:\/\//i.test(url) ? u.toString() : `${u.pathname}${u.search}${u.hash}`;
    } catch { return url; }
}

let inflight = {};
async function fetchPublicBrand(ref) {
    const cached = read(BRAND_KEY(ref));
    if (cached?.data && Date.now() - (cached.at || 0) < BRAND_TTL_MS) return cached.data;
    if (!inflight[ref]) {
        inflight[ref] = fetch(API_URL, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ intent: "agent_public_brand", ref }),
        })
            .then((r) => r.json())
            .then((j) => {
                const d = j?.status === 200 && j.data?.name
                    ? { name: j.data.name, phone: j.data.phone || "", email: j.data.email || "", logo: j.data.logo_data_url || "" }
                    : null;
                if (d) { write(BRAND_KEY(ref), { at: Date.now(), data: d }); return d; }
                // Only a definite "no such partner" drops the ref. Anything else (backend not
                // deployed yet, 5xx) is not cached, so the next page load tries again.
                if (j?.status === 404 && read(REF_KEY)?.ref === ref) write(REF_KEY, null);
                if (j?.status !== 404) console.warn("[partnerBrand] agent_public_brand failed:", j?.status, j?.message);
                return cached?.data || null;
            })
            .catch(() => cached?.data || null)   // offline: last known
            .finally(() => { delete inflight[ref]; });
    }
    return inflight[ref];
}

// Where a customer (signed in or not) sees a partner's branding:
//   1. the page the partner shared: a shared route or trip page whose URL has ?p=<ref>
//   2. booking pages opened from that page (flight, bus, hotel and their confirmations)
// Every other page is plain RouteChef, and visiting one ends the flow: a booking page reached
// later from home or search is plain RouteChef too. Kept per browser tab (sessionStorage).
// A signed-in partner sees their own branding everywhere.
const FLOW_KEY = "rc_partner_flow";       // the ref of the partner whose page this tab came from
const BOOKING_PATHS = ["/book-flight", "/booking-flight", "/book-bus", "/trip-planner/hotel"];

const urlRef = (search) => {
    try {
        const r = new URLSearchParams(search).get(PARTNER_PARAM);
        return r && REF_RE.test(r) && r !== ownPartnerRef() ? r : "";
    } catch { return ""; }
};

/** Partner ref branding the current page, or "" for plain RouteChef. */
function flowRefFor(pathname, search) {
    const r = urlRef(search);
    try {
        if (r) { sessionStorage.setItem(FLOW_KEY, r); return r; }
        if (BOOKING_PATHS.some((p) => pathname.startsWith(p))) return sessionStorage.getItem(FLOW_KEY) || "";
        sessionStorage.removeItem(FLOW_KEY);
    } catch { return r; }
    return "";
}

/** The partner ref of the page flow this tab is in ("" outside a partner's shared page). */
export function getFlowPartnerRef() {
    try {
        const r = urlRef(window.location.search);
        if (r) return r;
        return BOOKING_PATHS.some((p) => window.location.pathname.startsWith(p)) ? (sessionStorage.getItem(FLOW_KEY) || "") : "";
    } catch { return ""; }
}

/**
 * Branding to show on this page, or null for plain RouteChef.
 * { name, phone, email, logo, own } where own = the signed-in partner is looking at their own branding.
 */
export function usePartnerBrand() {
    const own = useAgentBranding();
    const { pathname, search } = useLocation();
    const ref = useMemo(() => { capturePartnerRef(search); return flowRefFor(pathname, search); }, [pathname, search]);
    const [brand, setBrand] = useState(null);
    useEffect(() => {
        if (own || !ref) { setBrand(null); return undefined; }
        let live = true;
        const c = read(BRAND_KEY(ref));
        setBrand(c?.data || null);
        fetchPublicBrand(ref).then((d) => { if (live) setBrand(d); });
        return () => { live = false; };
    }, [ref, !!own]); // eslint-disable-line react-hooks/exhaustive-deps

    if (own) return own.name ? { name: own.name, phone: own.phone, email: own.email, logo: own.logo, own: true } : null;
    return ref && brand ? { ...brand, own: false } : null;
}
